@file:OptIn(ExperimentalJsExport::class)
@file:JsExport

package game

import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.max
import kotlin.math.min
import kotlin.math.sin
import kotlin.math.sqrt

// ===== 底盘属性 =====
fun chassisSpeedMult(c: ChassisType): Double = when (c) {
    ChassisType.LIGHT -> GameConfig.LIGHT_SPEED_MULT
    ChassisType.MEDIUM -> GameConfig.MEDIUM_SPEED_MULT
    ChassisType.HEAVY -> GameConfig.HEAVY_SPEED_MULT
}

fun chassisHpMult(c: ChassisType): Double = when (c) {
    ChassisType.LIGHT -> GameConfig.LIGHT_HP_MULT
    ChassisType.MEDIUM -> GameConfig.MEDIUM_HP_MULT
    ChassisType.HEAVY -> GameConfig.HEAVY_HP_MULT
}

fun chassisArmor(c: ChassisType): Int = when (c) {
    ChassisType.LIGHT -> GameConfig.LIGHT_ARMOR
    ChassisType.MEDIUM -> GameConfig.MEDIUM_ARMOR
    ChassisType.HEAVY -> GameConfig.HEAVY_ARMOR
}

// 敌人底盘:随波次递进(轻甲 -> 中甲 -> 重甲)
private fun randomEnemyChassis(wave: Int): ChassisType {
    return when {
        wave < 5 -> ChassisType.LIGHT
        wave < 10 -> if (wave % 2 == 0) ChassisType.MEDIUM else ChassisType.LIGHT
        wave < 20 -> if (wave % 3 == 0) ChassisType.HEAVY else ChassisType.MEDIUM
        else -> ChassisType.HEAVY
    }
}

fun createInitialState(): GameState {
    val obstacles = generateObstacles(seed = 0, count = 10)
    return GameState(
        status = GameStatus.MENU,
        player = createTank(
            id = 0, x = 0.0, z = 0.0, isPlayer = true, hp = GameConfig.BASE_PLAYER_HP,
            chassis = ChassisType.MEDIUM
        ),
        enemies = emptyList(),
        bullets = emptyList(),
        lasers = emptyList(),
        effects = emptyList(),
        powerups = emptyList(),
        obstacles = obstacles,
        score = 0,
        wave = 0,
        kills = 0,
        nextId = 1,
        powerupTimer = GameConfig.POWERUP_SPAWN_INTERVAL,
        incomeTimer = GameConfig.INCOME_INTERVAL,
        coins = GameConfig.START_COINS,
        ownedWeapons = listOf(BulletType.STANDARD),
        selectedWeapon = BulletType.STANDARD,
        ownedChassis = listOf(ChassisType.LIGHT, ChassisType.MEDIUM),
        selectedChassis = ChassisType.MEDIUM,
        damageNumbers = emptyList(),
        debug = DebugState(),
        message = ""
    )
}

fun createTank(
    id: Int,
    x: Double,
    z: Double,
    isPlayer: Boolean,
    hp: Int,
    chassis: ChassisType = ChassisType.MEDIUM,
    weapon: BulletType = BulletType.STANDARD
): TankState = TankState(
    id = id,
    x = x,
    z = z,
    y = 0.0,
    vy = 0.0,
    angle = 0.0,
    turretAngle = 0.0,
    hp = hp,
    maxHp = hp,
    energy = GameConfig.MAX_ENERGY,
    maxEnergy = GameConfig.MAX_ENERGY,
    isPlayer = isPlayer,
    alive = true,
    fireCooldown = 0.0,
    speed = (if (isPlayer) GameConfig.BASE_PLAYER_SPEED else GameConfig.BASE_ENEMY_SPEED) * chassisSpeedMult(chassis),
    chassis = chassis,
    armor = chassisArmor(chassis),
    weapon = weapon
)

// ===== 波次敌人数量(随波次波动,波次越高越多)=====
// 公式:第 wave 波最少 minCount,最多 maxCount
fun waveMinEnemies(wave: Int): Int = when {
    wave <= 0 -> GameConfig.BASE_ENEMY_COUNT
    wave <= 5 -> 3 + wave
    wave <= 10 -> 8 + (wave - 5) / 2
    wave <= 20 -> 12 + (wave - 10) / 2
    wave <= 50 -> 16 + (wave - 20) / 3
    wave <= 100 -> 20 + (wave - 50) / 5
    else -> 30
}

fun waveMaxEnemies(wave: Int): Int = when {
    wave <= 0 -> GameConfig.BASE_ENEMY_COUNT + 2
    wave <= 5 -> 4 + wave * 2
    wave <= 10 -> 12 + wave / 2
    wave <= 20 -> 16 + (wave - 10) / 2
    wave <= 50 -> 22 + (wave - 20) / 3
    wave <= 100 -> 28 + (wave - 50) / 5
    else -> 40
}

// 伪随机(确定性,便于测试)
private fun pseudoRandom(seed: Int): Double {
    var s = seed * 9301 + 49297
    s = s % 233280
    return s.toDouble() / 233280.0
}

// 随机生成障碍物(避开中心出生区)
private fun generateObstacles(seed: Int, count: Int): List<ObstacleState> {
    return (0 until count).map { i ->
        val margin = GameConfig.ARENA_HALF - 6.0
        var x = (pseudoRandom(seed * 1000 + i * 7) * 2 - 1) * margin
        var z = (pseudoRandom(seed * 1000 + i * 13 + 5) * 2 - 1) * margin
        // 避开中心出生区域(半径 12)
        if (x * x + z * z < 12.0 * 12.0) {
            x = if (x >= 0) 13.0 else -13.0
            z = if (z >= 0) 13.0 else -13.0
        }
        val w = 3.0 + pseudoRandom(seed + i) * 5.0
        val h = 3.0 + pseudoRandom(seed + i + 1) * 5.0
        ObstacleState(id = i + 1, x = x, z = z, w = w, h = h)
    }
}

// 波次生成敌人:沿四周随机分布
private fun spawnWave(wave: Int, nextId: Int, player: TankState): List<TankState> {
    val minCount = waveMinEnemies(wave)
    val maxCount = waveMaxEnemies(wave)
    val count = minCount + (pseudoRandom(wave * 31 + 7).toInt() % (maxCount - minCount + 1))
    val hp = (GameConfig.BASE_ENEMY_HP + wave * 5).toInt()
    return (0 until count).map { i ->
        val chassis = randomEnemyChassis(wave)
        // 玩家周围随机环形分布,避免出生在玩家身边
        val angle = pseudoRandom(wave * 97 + i * 17) * 2 * PI
        val dist = GameConfig.ARENA_HALF * 0.5 + pseudoRandom(wave + i) * GameConfig.ARENA_HALF * 0.5
        val x = player.x + cos(angle) * dist
        val z = player.z + sin(angle) * dist
        val clampedX = max(-(GameConfig.ARENA_HALF - 6.0), min(GameConfig.ARENA_HALF - 6.0, x))
        val clampedZ = max(-(GameConfig.ARENA_HALF - 6.0), min(GameConfig.ARENA_HALF - 6.0, z))
        createTank(
            id = nextId + i,
            x = clampedX,
            z = clampedZ,
            isPlayer = false,
            hp = (hp * chassisHpMult(chassis)).toInt(),
            chassis = chassis,
            weapon = enemyWeapon(wave)
        )
    }
}

private fun enemyWeapon(wave: Int): BulletType = when {
    wave >= 18 -> BulletType.HOMING
    wave >= 12 -> BulletType.MISSILE
    wave >= 8 -> BulletType.LASER
    else -> BulletType.STANDARD
}

private fun clampToArena(value: Double): Double {
    val limit = GameConfig.ARENA_HALF - GameConfig.TANK_RADIUS
    return max(-limit, min(limit, value))
}

// 圆与 AABB 碰撞
private fun circleIntersectsObstacle(
    x: Double,
    z: Double,
    radius: Double,
    obstacle: ObstacleState
): Boolean {
    val hw = obstacle.w / 2
    val hh = obstacle.h / 2
    val closestX = max(obstacle.x - hw, min(x, obstacle.x + hw))
    val closestZ = max(obstacle.z - hh, min(z, obstacle.z + hh))
    val dx = x - closestX
    val dz = z - closestZ
    return dx * dx + dz * dz < radius * radius
}

// 点与 AABB 碰撞
private fun pointInObstacle(
    x: Double,
    z: Double,
    obstacle: ObstacleState
): Boolean {
    val hw = obstacle.w / 2
    val hh = obstacle.h / 2
    return x in (obstacle.x - hw)..(obstacle.x + hw) &&
        z in (obstacle.z - hh)..(obstacle.z + hh)
}

fun gameReducer(state: GameState = createInitialState(), action: GameAction): GameState {
    return when (action) {
        is GameAction.StartGame -> {
            val fresh = createInitialState().copy(
                status = GameStatus.PLAYING,
                player = createTank(
                    id = 0, x = 0.0, z = 0.0, isPlayer = true,
                    hp = (GameConfig.BASE_PLAYER_HP * chassisHpMult(state.selectedChassis)).toInt(),
                    chassis = state.selectedChassis,
                    weapon = state.selectedWeapon
                ),
                obstacles = generateObstacles(seed = 1, count = 14),
                coins = state.coins,
                ownedWeapons = state.ownedWeapons,
                selectedWeapon = state.selectedWeapon,
                ownedChassis = state.ownedChassis,
                selectedChassis = state.selectedChassis
            )
            // 生成第一波
            val player = fresh.player
            fresh.copy(
                enemies = spawnWave(1, 1, player),
                nextId = 1 + waveMaxEnemies(1)
            )
        }

        is GameAction.Restart -> {
            val fresh = createInitialState().copy(
                status = GameStatus.PLAYING,
                player = createTank(
                    id = 0, x = 0.0, z = 0.0, isPlayer = true,
                    hp = (GameConfig.BASE_PLAYER_HP * chassisHpMult(state.selectedChassis)).toInt(),
                    chassis = state.selectedChassis,
                    weapon = state.selectedWeapon
                ),
                obstacles = generateObstacles(seed = 1, count = 14),
                coins = state.coins,
                ownedWeapons = state.ownedWeapons,
                selectedWeapon = state.selectedWeapon,
                ownedChassis = state.ownedChassis,
                selectedChassis = state.selectedChassis
            )
            val player = fresh.player
            fresh.copy(
                enemies = spawnWave(1, 1, player),
                nextId = 1 + waveMaxEnemies(1)
            )
        }

        is GameAction.ToMenu -> {
            // 返回菜单:保留金币与已购武器/底盘,只重置战场
            createInitialState().copy(
                coins = state.coins,
                ownedWeapons = state.ownedWeapons,
                selectedWeapon = state.selectedWeapon,
                ownedChassis = state.ownedChassis,
                selectedChassis = state.selectedChassis,
                score = state.score,
                kills = state.kills,
                wave = state.wave
            )
        }

        is GameAction.OpenGarage -> state.copy(status = GameStatus.GARAGE)

        is GameAction.OpenShop -> state.copy(status = GameStatus.SHOP)

        is GameAction.Pause -> {
            if (state.status == GameStatus.PLAYING) state.copy(status = GameStatus.PAUSED) else state
        }

        is GameAction.Resume -> {
            if (state.status == GameStatus.PAUSED) state.copy(status = GameStatus.PLAYING) else state
        }

        is GameAction.SelectChassis -> {
            if (action.chassis !in state.ownedChassis) return state
            state.copy(selectedChassis = action.chassis)
        }

        is GameAction.BuyWeapon -> {
            // 已拥有则直接装备
            if (action.weapon in state.ownedWeapons) {
                return state.copy(selectedWeapon = action.weapon)
            }
            val price = weaponPrice(action.weapon)
            if (state.coins < price) return state
            state.copy(
                coins = state.coins - price,
                ownedWeapons = state.ownedWeapons + action.weapon,
                selectedWeapon = action.weapon
            )
        }

        is GameAction.BuyChassis -> {
            // 已拥有则直接装备
            if (action.chassis in state.ownedChassis) {
                return state.copy(selectedChassis = action.chassis)
            }
            val price = chassisPrice(action.chassis)
            if (state.coins < price) return state
            state.copy(
                coins = state.coins - price,
                ownedChassis = state.ownedChassis + action.chassis,
                selectedChassis = action.chassis
            )
        }

        is GameAction.Tick -> tick(state, action.input, action.dt)

        // ===== 调试工具 =====
        is GameAction.AddCoins -> state.copy(coins = max(0, state.coins + action.amount))

        is GameAction.GrantWeapon -> state.copy(
            ownedWeapons = if (action.weapon in state.ownedWeapons) state.ownedWeapons else state.ownedWeapons + action.weapon,
            selectedWeapon = action.weapon
        )

        is GameAction.GrantChassis -> state.copy(
            ownedChassis = if (action.chassis in state.ownedChassis) state.ownedChassis else state.ownedChassis + action.chassis,
            selectedChassis = action.chassis
        )

        is GameAction.SetGodMode -> {
            // 无敌:满血 + 开启 godMode(免疫伤害由 tick 处理)
            val p = state.player.copy(hp = state.player.maxHp)
            state.copy(player = p, debug = state.debug.copy(godMode = action.on))
        }

        is GameAction.GrantSkill -> {
            val p = when (action.skill) {
                SkillType.DASH -> state.player.copy(dashCooldown = 0.0)
                SkillType.SHIELD -> state.player.copy(shieldTimer = GameConfig.SHIELD_SKILL_DURATION, shieldCooldown = 0.0)
                SkillType.NUKE -> state.player.copy(nukeCooldown = 0.0)
            }
            state.copy(player = p)
        }

        is GameAction.GrantPowerup -> {
            state.copy(player = applyPowerup(state.player, action.powerup))
        }

        is GameAction.ToggleDamageNumbers -> state.copy(debug = state.debug.copy(showDamage = action.on))

        is GameAction.ToggleDebugPanel -> state.copy(debug = state.debug.copy(panelOpen = !state.debug.panelOpen))
    }
}

private fun weaponPrice(w: BulletType): Int = when (w) {
    BulletType.STANDARD -> 0
    BulletType.LASER -> GameConfig.WEAPON_LASER_PRICE
    BulletType.MISSILE -> GameConfig.WEAPON_MISSILE_PRICE
    BulletType.HOMING -> GameConfig.WEAPON_HOMING_PRICE
    BulletType.MELEE -> GameConfig.WEAPON_MELEE_PRICE
    BulletType.SHOTGUN -> GameConfig.WEAPON_SHOTGUN_PRICE
    BulletType.PLASMA -> GameConfig.WEAPON_PLASMA_PRICE
    BulletType.GATLING -> GameConfig.WEAPON_GATLING_PRICE
    BulletType.EMP -> GameConfig.WEAPON_EMP_PRICE
    BulletType.GRENADE -> GameConfig.WEAPON_GRENADE_PRICE
    BulletType.FLAME -> GameConfig.WEAPON_FLAME_PRICE
    BulletType.OBLIVION -> GameConfig.OBLIVION_PRICE
}

private fun chassisPrice(c: ChassisType): Int = when (c) {
    ChassisType.LIGHT -> 0
    ChassisType.MEDIUM -> GameConfig.CHASSIS_MEDIUM_PRICE
    ChassisType.HEAVY -> GameConfig.CHASSIS_HEAVY_PRICE
}

// ===== 道具效果 =====
private fun applyPowerup(tank: TankState, type: PowerupType): TankState {
    return when (type) {
        PowerupType.HEALTH -> tank.copy(hp = min(tank.maxHp, tank.hp + 30))
        PowerupType.SPEED -> tank.copy(speedTimer = GameConfig.BUFF_DURATION)
        PowerupType.SHIELD -> tank.copy(shieldTimer = GameConfig.BUFF_DURATION)
        PowerupType.RAPID -> tank.copy(rapidTimer = GameConfig.BUFF_DURATION)
        PowerupType.MULTI -> tank.copy(multiTimer = GameConfig.BUFF_DURATION)
    }
}

// ===== 主循环 =====
private fun tick(state: GameState, input: PlayerInput, dt: Double): GameState {
    if (state.status != GameStatus.PLAYING) return state

    // 1. 玩家移动 + 跳跃 + 冷却
    var player = if (state.player.alive) {
        updatePlayer(state.player, input, dt, state.obstacles)
    } else {
        state.player
    }

    // 1.5 技能触发(消耗能量)
    var nukeTriggered = false
    if (player.alive) {
        if (input.dash && player.dashCooldown <= 0.0 && player.energy >= GameConfig.ENERGY_DASH_COST) {
            player = player.copy(
                dashCooldown = GameConfig.DASH_COOLDOWN,
                dashTime = GameConfig.DASH_DURATION,
                energy = player.energy - GameConfig.ENERGY_DASH_COST
            )
        }
        if (input.shield && player.shieldCooldown <= 0.0 && player.energy >= GameConfig.ENERGY_SHIELD_COST) {
            player = player.copy(
                shieldTimer = GameConfig.SHIELD_SKILL_DURATION,
                shieldCooldown = GameConfig.SHIELD_SKILL_COOLDOWN,
                energy = player.energy - GameConfig.ENERGY_SHIELD_COST
            )
        }
        if (input.nuke && player.nukeCooldown <= 0.0 && player.energy >= GameConfig.ENERGY_NUKE_COST) {
            player = player.copy(
                nukeCooldown = GameConfig.NUKE_COOLDOWN,
                energy = player.energy - GameConfig.ENERGY_NUKE_COST
            )
            nukeTriggered = true
        }
    }
    player = if (player.dashTime > 0.0) {
        val dashAngle = player.turretAngle
        player.copy(
            x = clampToArena(player.x + sin(dashAngle) * player.speed * GameConfig.DASH_SPEED_MULT * dt),
            z = clampToArena(player.z + cos(dashAngle) * player.speed * GameConfig.DASH_SPEED_MULT * dt)
        )
    } else player

    // 2. 敌人 AI
    val enemiesBefore = state.enemies
    val enemyResults = enemiesBefore.map { enemy ->
        if (enemy.alive) updateEnemy(enemy, player, enemiesBefore, dt, state.obstacles)
        else enemy.copy(fireCooldown = 0.0) to false
    }
    var enemies = enemyResults.map { it.first }

    // 核弹
    if (nukeTriggered) {
        enemies = enemies.map { enemy ->
            if (enemy.alive) {
                val newHp = max(0, enemy.hp - GameConfig.NUKE_DAMAGE)
                enemy.copy(hp = newHp, alive = newHp > 0)
            } else enemy
        }
    }

    // 3. 生成新子弹 / 激光
    var nextId = state.nextId
    val newBullets = mutableListOf<BulletState>()
    val newLasers = mutableListOf<LaserBeamState>()
    val newEffects = mutableListOf<EffectState>()
    val newDamageNumbers = mutableListOf<DamageNumberState>()
    val fireCooldown = if (player.rapidTimer > 0.0) GameConfig.FIRE_COOLDOWN * GameConfig.RAPID_BUFF_MULT else GameConfig.FIRE_COOLDOWN
    // 近战武器不发射子弹(由 applyMelee 直接造成范围伤害)
    val isMelee = player.weapon == BulletType.MELEE
    var meleeFired = false
    if (player.alive && input.fire && player.fireCooldown <= 0.0) {
        when {
            isMelee -> {
                meleeFired = true
                // 电击特效在命中后生成(见步骤6)
            }
            player.weapon == BulletType.LASER -> {
                // 激光:即时射线检测,立即伤害,生成光束特效
                val angles = if (player.multiTimer > 0.0) {
                    val count = GameConfig.MULTI_SHOT_COUNT
                    val spread = GameConfig.MULTI_SHOT_ANGLE
                    (0 until count).map { i -> player.turretAngle + (i - (count - 1) / 2.0) * spread }
                } else {
                    listOf(player.turretAngle)
                }
                angles.forEach { angle ->
                    val beam = raycastLaser(player, angle, enemies, state.obstacles)
                    newLasers += beam.beam.copy(id = nextId)
                    nextId += 1
                    // 对命中敌人造成伤害(穿透),并生成伤害飘字
                    val laserResult = applyLaserDamage(enemies, player, beam, GameConfig.LASER_DAMAGE, nextId)
                    enemies = laserResult.enemies
                    nextId += laserResult.damageNumbers.size
                    newDamageNumbers += laserResult.damageNumbers
                }
            }
            player.weapon == BulletType.EMP -> {
                // EMP:发射脉冲弹,命中后范围眩晕
                newBullets += fireWeapon(player, nextId, player.turretAngle)
                nextId += 1
                // EMP 冲击波特效
                newEffects += EffectState(
                    id = nextId, type = EffectType.EMP_BLAST,
                    x = player.x, z = player.z,
                    life = 0.4, radius = GameConfig.EMP_RADIUS
                )
                nextId += 1
            }
            player.weapon == BulletType.SHOTGUN -> {
                // 霰弹:多发弹片散射
                for (i in 0 until GameConfig.SHOTGUN_PELLETS) {
                    val offset = (i - (GameConfig.SHOTGUN_PELLETS - 1) / 2.0) * GameConfig.SHOTGUN_SPREAD
                    newBullets += fireWeapon(player, nextId, player.turretAngle + offset)
                    nextId += 1
                }
            }
            // ===== 🚀 湮灭之核：多重射击 + 全屏追踪 =====
            player.weapon == BulletType.OBLIVION -> {
                // 多重射击
                val count = GameConfig.OBLIVION_MULTI_SHOT
                for (i in 0 until count) {
                    val angleOffset = (i.toDouble() / count) * 2 * PI
                    newBullets += fireWeapon(player, nextId + i, player.turretAngle + angleOffset)
                }
                nextId += count
            }
            else -> {
                newBullets += fireWeapon(player, nextId, player.turretAngle)
                if (player.multiTimer > 0.0) {
                    for (i in 0 until GameConfig.MULTI_SHOT_COUNT) {
                        val offset = (i - (GameConfig.MULTI_SHOT_COUNT - 1) / 2.0) * GameConfig.MULTI_SHOT_ANGLE
                        newBullets += fireWeapon(player, nextId + i + 1, player.turretAngle + offset)
                    }
                    nextId += GameConfig.MULTI_SHOT_COUNT + 1
                } else {
                    nextId += 1
                }
            }
        }
        // 近战 / 加特林使用独立冷却
        val cd = when {
            isMelee -> GameConfig.MELEE_COOLDOWN
            player.weapon == BulletType.GATLING -> GameConfig.GATLING_COOLDOWN
            else -> fireCooldown
        }
        player = player.copy(fireCooldown = cd)
    }
    // 敌人开火
    var enemiesAfterFire = enemies
    enemyResults.forEach { (enemy, wantsToFire) ->
        if (enemy.alive && wantsToFire && enemy.fireCooldown <= 0.0) {
            newBullets += fireWeapon(enemy, nextId, enemy.turretAngle)
            nextId += 1
            enemiesAfterFire = enemiesAfterFire.map {
                if (it.id == enemy.id) it.copy(fireCooldown = GameConfig.FIRE_COOLDOWN) else it
            }
        }
    }
    val cooledEnemiesAfterFire = enemiesAfterFire.map { it.copy(fireCooldown = max(0.0, it.fireCooldown - dt)) }

    // 4. 移动子弹(追踪弹需要转向)
    val moved = (state.bullets + newBullets).mapNotNull { bullet ->
        moveBullet(bullet, state, dt)
    }

    // 5. 子弹 vs 障碍物/边界
    val aliveBullets = moved.filter { bullet ->
        val inBounds = abs(bullet.x) < GameConfig.ARENA_HALF && abs(bullet.z) < GameConfig.ARENA_HALF
        val notInObstacle = state.obstacles.none { pointInObstacle(bullet.x, bullet.z, it) }
        inBounds && notInObstacle
    }

    // 6. 子弹 vs 坦克
    // 记录伤害前的血量(用于生成飘字)
    val playerHpBefore = player.hp
    val enemyHpBefore = cooledEnemiesAfterFire.associate { it.id to it.hp }
    var bulletHit = applyBulletDamage(player, cooledEnemiesAfterFire, aliveBullets, state.debug.godMode)
    player = bulletHit.player
    var updatedEnemies = bulletHit.enemies
    var bullets = bulletHit.bullets

    // 近战武器:玩家直射电击 + 连锁
    if (meleeFired && player.alive) {
        val meleeResult = applyMelee(player, updatedEnemies, nextId)
        updatedEnemies = meleeResult.enemies
        nextId += meleeResult.damageNumbers.size
        newDamageNumbers += meleeResult.damageNumbers
        // 电击特效:竖电柱(玩家)+ 连锁电弧(各连锁点)
        if (meleeResult.chainPoints.size > 1) {
            // 玩家起点竖电柱
            newEffects += EffectState(
                id = nextId, type = EffectType.MELEE_SHOCK,
                x = player.x, z = player.z,
                life = 0.25, radius = GameConfig.MELEE_RADIUS
            )
            nextId += 1
            // 每个连锁点的竖电柱
            meleeResult.chainPoints.drop(1).forEach { (ex, ez) ->
                newEffects += EffectState(
                    id = nextId, type = EffectType.MELEE_SHOCK,
                    x = ex, z = ez,
                    life = 0.2, radius = 2.5
                )
                nextId += 1
            }
        }
    }

    // 生成伤害飘字(对比血量变化)
    if (state.debug.showDamage) {
        // 敌人受到的伤害(玩家造成,黄色)
        updatedEnemies.forEach { enemy ->
            val before = enemyHpBefore[enemy.id] ?: return@forEach
            val taken = before - enemy.hp
            if (taken > 0) {
                newDamageNumbers += DamageNumberState(
                    id = nextId, x = enemy.x, z = enemy.z,
                    amount = taken, isPlayer = true, life = 1.0
                )
                nextId += 1
            }
        }
        // 玩家受到的伤害(红色)
        if (playerHpBefore > player.hp) {
            newDamageNumbers += DamageNumberState(
                id = nextId, x = player.x, z = player.z,
                amount = playerHpBefore - player.hp, isPlayer = false, life = 1.0
            )
            nextId += 1
        }
    }

    // 7. 冷却 / buff 计时递减
    player = player.copy(
        fireCooldown = max(0.0, player.fireCooldown - dt),
        dashCooldown = max(0.0, player.dashCooldown - dt),
        dashTime = max(0.0, player.dashTime - dt),
        shieldCooldown = max(0.0, player.shieldCooldown - dt),
        nukeCooldown = max(0.0, player.nukeCooldown - dt),
        shieldTimer = max(0.0, player.shieldTimer - dt),
        speedTimer = max(0.0, player.speedTimer - dt),
        rapidTimer = max(0.0, player.rapidTimer - dt),
        multiTimer = max(0.0, player.multiTimer - dt),
        stunTimer = max(0.0, player.stunTimer - dt),
        // 能量恢复
        energy = min(GameConfig.MAX_ENERGY, player.energy + GameConfig.ENERGY_REGEN * dt)
    )
    var cooledEnemies = updatedEnemies.map {
        it.copy(
            fireCooldown = max(0.0, it.fireCooldown - dt),
            shieldTimer = max(0.0, it.shieldTimer - dt),
            speedTimer = max(0.0, it.speedTimer - dt),
            stunTimer = max(0.0, it.stunTimer - dt),
            burnTimer = max(0.0, it.burnTimer - dt)
        )
    }

    // 7.5 燃烧伤害:burnTimer > 0 时每秒扣血
    val burnedEnemies = cooledEnemies.map { enemy ->
        if (enemy.alive && enemy.burnTimer > 0.0 && enemy.shieldTimer <= 0.0) {
            val dmg = GameConfig.FLAME_BURN_TICK.toInt().coerceAtLeast(1)
            val newHp = max(0, enemy.hp - dmg)
            if (newHp < enemy.hp && state.debug.showDamage) {
                newDamageNumbers += DamageNumberState(
                    id = nextId, x = enemy.x, z = enemy.z,
                    amount = dmg, isPlayer = true, life = 1.0
                )
                nextId += 1
            }
            enemy.copy(hp = newHp, alive = newHp > 0)
        } else enemy
    }
    // 玩家燃烧
    if (player.alive && player.burnTimer > 0.0 && player.shieldTimer <= 0.0 && !state.debug.godMode) {
        val dmg = GameConfig.FLAME_BURN_TICK.toInt().coerceAtLeast(1)
        player = player.copy(hp = max(0, player.hp - dmg), alive = player.hp - dmg > 0)
        if (state.debug.showDamage) {
            newDamageNumbers += DamageNumberState(
                id = nextId, x = player.x, z = player.z,
                amount = dmg, isPlayer = false, life = 1.0
            )
            nextId += 1
        }
    }
    cooledEnemies = burnedEnemies

    // 武器特效生命周期递减
    var effects = (state.effects + newEffects).mapNotNull { effect ->
        val life = effect.life - dt
        if (life <= 0.0) null else effect.copy(life = life)
    }

    // 伤害飘字生命周期递减
    var damageNumbers = (state.damageNumbers + newDamageNumbers).mapNotNull { d ->
        val life = d.life - dt
        if (life <= 0.0) null else d.copy(life = life)
    }

    // 激光光束生命周期递减
    var lasers = (state.lasers + newLasers).mapNotNull { laser ->
        val life = laser.life - dt
        if (life <= 0.0) null else laser.copy(life = life)
    }

    // 8. 计分与金币
    var kills = state.kills
    var score = state.score
    var coins = state.coins
    cooledEnemies.forEach { enemy ->
        val wasAlive = enemiesBefore.firstOrNull { it.id == enemy.id }?.alive == true
        if (wasAlive && !enemy.alive) {
            kills += 1
            score += 100
            coins += GameConfig.KILL_REWARD
        }
    }

    // 8.5 被动收入:存活工资,每 INCOME_INTERVAL 秒发放
    var incomeTimer = state.incomeTimer - dt
    if (incomeTimer <= 0.0 && player.alive) {
        coins += GameConfig.INCOME_AMOUNT
        incomeTimer += GameConfig.INCOME_INTERVAL
    }

    // 9. 道具刷新 + 拾取
    var powerupTimer = state.powerupTimer - dt
    var powerups = state.powerups.mapNotNull { p ->
        val life = p.life - dt
        if (life <= 0.0) null else p.copy(life = life)
    }
    if (powerupTimer <= 0.0 && powerups.size < GameConfig.POWERUP_MAX_ON_FIELD) {
        val spawn = spawnPowerup(nextId, listOf(player) + enemies)
        if (spawn != null) {
            powerups = powerups + spawn
            nextId += 1
        }
        powerupTimer = GameConfig.POWERUP_SPAWN_INTERVAL
    }
    var finalPlayer = player
    var finalEnemies = cooledEnemies
    (listOf(player) + cooledEnemies).filter { it.alive }.forEach { tank ->
        val nearby = powerups.filter { p ->
            val dx = tank.x - p.x
            val dz = tank.z - p.z
            dx * dx + dz * dz < GameConfig.POWERUP_RADIUS * GameConfig.POWERUP_RADIUS
        }
        nearby.forEach { p ->
            powerups = powerups.filter { it.id != p.id }
            if (tank.id == finalPlayer.id) {
                finalPlayer = applyPowerup(finalPlayer, p.type)
            } else {
                finalEnemies = finalEnemies.map { e ->
                    if (e.id == tank.id) {
                        when (p.type) {
                            PowerupType.SHIELD -> e.copy(shieldTimer = GameConfig.BUFF_DURATION)
                            PowerupType.SPEED -> e.copy(speedTimer = GameConfig.BUFF_DURATION)
                            else -> e
                        }
                    } else e
                }
            }
        }
    }
    player = finalPlayer
    val pickupEnemies = finalEnemies

    // 10. 玩家死亡
    if (!player.alive) {
        return state.copy(
            player = player,
            enemies = pickupEnemies,
            bullets = bullets,
            lasers = lasers,
            effects = effects,
            damageNumbers = damageNumbers,
            powerups = powerups,
            kills = kills,
            score = score,
            coins = coins,
            powerupTimer = powerupTimer,
            incomeTimer = incomeTimer,
            status = GameStatus.GAME_OVER,
            message = "你被摧毁了!得分: $score · 金币: $coins"
        )
    }

    // 11. 敌人全灭 => 下一波(随机障碍物重新生成)
    val hasEnemies = enemiesBefore.isNotEmpty()
    val allDead = pickupEnemies.all { !it.alive }
    if (hasEnemies && allDead) {
        val nextWave = state.wave + 1
        val newObstacles = generateObstacles(seed = nextWave, count = 8 + (nextWave % 8))
        val newEnemies = spawnWave(nextWave, nextId, player)
        player = player.copy(hp = player.maxHp, alive = true, y = 0.0, vy = 0.0)
        return state.copy(
            player = player,
            enemies = newEnemies,
            bullets = emptyList(),
            lasers = emptyList(),
            effects = emptyList(),
            damageNumbers = emptyList(),
            powerups = emptyList(),
            obstacles = newObstacles,
            wave = nextWave,
            kills = kills,
            score = score + 50,
            coins = coins + GameConfig.WAVE_BONUS,
            nextId = nextId + waveMaxEnemies(nextWave),
            powerupTimer = GameConfig.POWERUP_SPAWN_INTERVAL,
            incomeTimer = GameConfig.INCOME_INTERVAL,
            message = "第 ${nextWave + 1} 波来袭!敌人 ${waveMinEnemies(nextWave)}-${waveMaxEnemies(nextWave)} 辆"
        )
    }

    return state.copy(
        player = player,
        enemies = pickupEnemies,
        bullets = bullets,
        lasers = lasers,
        effects = effects,
        damageNumbers = damageNumbers,
        powerups = powerups,
        kills = kills,
        score = score,
        coins = coins,
        nextId = nextId,
        powerupTimer = powerupTimer,
        incomeTimer = incomeTimer
    )
}

// 激光射线检测:沿角度方向找到第一个命中点(障碍物/敌人/边界)
private data class LaserHit(
    val beam: LaserBeamState,
    val hitEnemies: List<TankState>  // 被激光击中的敌人(按距离排序)
)

private fun raycastLaser(
    shooter: TankState,
    angle: Double,
    enemies: List<TankState>,
    obstacles: List<ObstacleState>
): LaserHit {
    val startX = shooter.x + sin(angle) * (GameConfig.TANK_RADIUS + 0.6)
    val startZ = shooter.z + cos(angle) * (GameConfig.TANK_RADIUS + 0.6)
    val dirX = sin(angle)
    val dirZ = cos(angle)

    // 步进采样,找到最近命中点
    val stepSize = 0.5
    var hitX = startX
    var hitZ = startZ
    val hitEnemies = mutableListOf<TankState>()

    var dist = stepSize
    while (dist <= GameConfig.ARENA_HALF * 2) {
        val px = startX + dirX * dist
        val pz = startZ + dirZ * dist
        // 边界
        if (abs(px) > GameConfig.ARENA_HALF || abs(pz) > GameConfig.ARENA_HALF) {
            hitX = px
            hitZ = pz
            break
        }
        // 障碍物
        if (obstacles.any { pointInObstacle(px, pz, it) }) {
            hitX = px
            hitZ = pz
            break
        }
        // 敌人
        var hitEnemy = false
        for (enemy in enemies) {
            if (enemy.id == shooter.id || !enemy.alive) continue
            val dx = px - enemy.x
            val dz = pz - enemy.z
            if (dx * dx + dz * dz < GameConfig.TANK_RADIUS * GameConfig.TANK_RADIUS) {
                hitX = px
                hitZ = pz
                hitEnemies += enemy
                hitEnemy = true
                break
            }
        }
        if (hitEnemy) break
        dist += stepSize
    }

    return LaserHit(
        beam = LaserBeamState(
            id = 0,
            x0 = startX,
            z0 = startZ,
            x1 = hitX,
            z1 = hitZ,
            life = 0.15,
            ownerId = shooter.id
        ),
        hitEnemies = hitEnemies
    )
}

// 激光对命中敌人造成穿透伤害,并返回伤害飘字
private data class LaserDamageResult(
    val enemies: List<TankState>,
    val damageNumbers: List<DamageNumberState>
)

private fun applyLaserDamage(
    enemies: List<TankState>,
    shooter: TankState,
    hit: LaserHit,
    damage: Int,
    nextId: Int
): LaserDamageResult {
    val numbers = mutableListOf<DamageNumberState>()
    if (hit.hitEnemies.isEmpty()) return LaserDamageResult(enemies, numbers)
    var id = nextId
    val updated = enemies.map { enemy ->
        if (enemy.id in hit.hitEnemies.map { it.id }) {
            if (enemy.shieldTimer > 0.0 || enemy.y > GameConfig.JUMP_DODGE_HEIGHT) return@map enemy
            val dmg = (damage * (100 - enemy.armor) / 100.0).toInt().coerceAtLeast(1)
            val newHp = max(0, enemy.hp - dmg)
            numbers += DamageNumberState(
                id = id, x = enemy.x, z = enemy.z,
                amount = dmg, isPlayer = true, life = 1.0
            )
            id += 1
            enemy.copy(hp = newHp, alive = newHp > 0)
        } else enemy
    }
    return LaserDamageResult(updated, numbers)
}

// 按武器类型发射子弹
private fun fireWeapon(tank: TankState, id: Int, angle: Double): BulletState {
    val bx = tank.x + sin(angle) * (GameConfig.TANK_RADIUS + 0.6)
    val bz = tank.z + cos(angle) * (GameConfig.TANK_RADIUS + 0.6)
    val speed: Double
    val damage: Int
    val type: BulletType
    val life: Double
    val pierce: Int
    when (tank.weapon) {
        BulletType.STANDARD -> {
            speed = GameConfig.STANDARD_SPEED
            damage = GameConfig.STANDARD_DAMAGE
            type = BulletType.STANDARD
            life = GameConfig.BULLET_LIFE
            pierce = 0
        }
        BulletType.LASER -> {
            speed = GameConfig.LASER_SPEED
            damage = GameConfig.LASER_DAMAGE
            type = BulletType.LASER
            life = GameConfig.BULLET_LIFE
            pierce = GameConfig.LASER_PIERCE
        }
        BulletType.MISSILE -> {
            speed = GameConfig.MISSILE_SPEED
            damage = GameConfig.MISSILE_DAMAGE
            type = BulletType.MISSILE
            life = GameConfig.MISSILE_LIFE
            pierce = 0
        }
        BulletType.HOMING -> {
            speed = GameConfig.HOMING_SPEED
            damage = GameConfig.HOMING_DAMAGE
            type = BulletType.HOMING
            life = GameConfig.BULLET_LIFE + 0.5
            pierce = 0
        }
        BulletType.MELEE -> {
            // 近战不产生飞行子弹
            speed = 0.0
            damage = 0
            type = BulletType.MELEE
            life = 0.0
            pierce = 0
        }
        BulletType.SHOTGUN -> {
            speed = GameConfig.SHOTGUN_SPEED
            damage = GameConfig.SHOTGUN_DAMAGE
            type = BulletType.SHOTGUN
            life = GameConfig.BULLET_LIFE
            pierce = 0
        }
        BulletType.PLASMA -> {
            speed = GameConfig.PLASMA_SPEED
            damage = GameConfig.PLASMA_DAMAGE
            type = BulletType.PLASMA
            life = GameConfig.PLASMA_LIFE
            pierce = 0
        }
        BulletType.GATLING -> {
            speed = GameConfig.GATLING_SPEED
            damage = GameConfig.GATLING_DAMAGE
            type = BulletType.GATLING
            life = GameConfig.BULLET_LIFE
            pierce = 0
        }
        BulletType.EMP -> {
            speed = GameConfig.EMP_SPEED
            damage = GameConfig.EMP_DAMAGE
            type = BulletType.EMP
            life = GameConfig.BULLET_LIFE + 0.3
            pierce = 0
        }
        BulletType.GRENADE -> {
            speed = GameConfig.GRENADE_SPEED
            damage = GameConfig.GRENADE_DAMAGE
            type = BulletType.GRENADE
            life = GameConfig.GRENADE_LIFE
            pierce = 0
        }
        BulletType.FLAME -> {
            speed = GameConfig.FLAME_SPEED
            damage = GameConfig.FLAME_DAMAGE
            type = BulletType.FLAME
            life = GameConfig.FLAME_LIFE
            pierce = 0
        }
        BulletType.OBLIVION -> {
            speed = GameConfig.OBLIVION_SPEED
            damage = GameConfig.OBLIVION_DAMAGE
            type = BulletType.OBLIVION
            life = GameConfig.OBLIVION_LIFE
            pierce = GameConfig.OBLIVION_PIERCE
        }
    }
    return BulletState(
        id = id,
        x = bx,
        z = bz,
        vx = sin(angle) * speed,
        vz = cos(angle) * speed,
        ownerId = tank.id,
        damage = damage,
        life = life,
        type = type,
        pierce = pierce
    )
}

// 移动子弹(追踪弹转向)
private fun moveBullet(bullet: BulletState, state: GameState, dt: Double): BulletState? {
    val life = bullet.life - dt
    if (life <= 0.0) return null

    var vx = bullet.vx
    var vz = bullet.vz
    var x = bullet.x
    var z = bullet.z

    if (bullet.type == BulletType.HOMING) {
        // 追踪最近的敌人
        val targets = state.enemies.filter { it.alive && it.id != bullet.ownerId }
        val target = targets.minByOrNull { e ->
            val dx = e.x - bullet.x
            val dz = e.z - bullet.z
            dx * dx + dz * dz
        }
        if (target != null) {
            val targetAngle = atan2(target.x - bullet.x, target.z - bullet.z)
            val curAngle = atan2(bullet.vx, bullet.vz)
            val diff = normalizeAngle(targetAngle - curAngle)
            val turn = max(-GameConfig.HOMING_TURN * dt, min(GameConfig.HOMING_TURN * dt, diff))
            val newAngle = curAngle + turn
            vx = sin(newAngle) * GameConfig.HOMING_SPEED
            vz = cos(newAngle) * GameConfig.HOMING_SPEED
        }
    }

    x += vx * dt
    z += vz * dt
    return bullet.copy(x = x, z = z, vx = vx, vz = vz, life = life)
}

// 子弹命中坦克(考虑护甲减伤、跳跃闪避、激光穿透、导弹范围)
private fun applyBulletDamage(
    player: TankState,
    enemies: List<TankState>,
    bullets: List<BulletState>,
    godMode: Boolean = false
): BulletHitResult {
    var p = player
    var en = enemies
    var remaining = bullets.toMutableList()
    
    // ===== 🚀 OBLIVION：全屏核爆 =====
    val oblivionBullets = remaining.filter { it.type == BulletType.OBLIVION }
    if (oblivionBullets.isNotEmpty()) {
        oblivionBullets.forEach { bullet ->
            // 对全屏所有敌人造成毁灭伤害
            en = en.map { enemy ->
                if (enemy.alive && enemy.id != bullet.ownerId) {
                    val dist = sqrt((enemy.x - bullet.x).let { it*it } + (enemy.z - bullet.z).let { it*it })
                    // 根据距离递减伤害，但即使边缘也至少造成 10000 伤害
                    val dmgMultiplier = max(0.1, 1.0 - (dist / GameConfig.OBLIVION_RADIUS))
                    val dmg = (GameConfig.OBLIVION_DAMAGE * dmgMultiplier).toInt().coerceAtLeast(10000)
                    val newHp = max(0, enemy.hp - dmg)
                    enemy.copy(
                        hp = newHp, 
                        alive = newHp > 0,
                        stunTimer = 10.0,  // 永久眩晕
                        burnTimer = 10.0
                    )
                } else enemy
            }
            // 对玩家自身无害（防止误伤）
            remaining.remove(bullet)
        }
        // 生成全屏特效标记（后续渲染）
    }

    // 玩家被击中
    if (p.alive) {
        for (bullet in bullets) {
            if (bullet.ownerId == p.id) continue
            if (p.shieldTimer > 0.0 || p.y > GameConfig.JUMP_DODGE_HEIGHT) continue
            if (circleHit(bullet.x, bullet.z, p.x, p.z)) {
                if (!godMode) {
                    val dmg = (bullet.damage * (100 - p.armor) / 100.0).toInt().coerceAtLeast(1)
                    // 火焰点燃玩家
                    val burn = if (bullet.type == BulletType.FLAME) GameConfig.FLAME_BURN_TIME else p.burnTimer
                    p = p.copy(hp = max(0, p.hp - dmg), alive = p.hp - dmg > 0, burnTimer = burn)
                }
                remaining = consumeBullet(remaining, bullet)
            }
        }
    }

    // 敌人被击中
    bullets.forEach { bullet ->
        if (!remaining.contains(bullet)) return@forEach
        val hitIndex = en.indexOfFirst { enemy ->
            enemy.id != bullet.ownerId && enemy.alive &&
                enemy.shieldTimer <= 0.0 && enemy.y <= GameConfig.JUMP_DODGE_HEIGHT &&
                circleHit(bullet.x, bullet.z, enemy.x, enemy.z)
        }
        if (hitIndex >= 0) {
            val enemy = en[hitIndex]
            val dmg = (bullet.damage * (100 - enemy.armor) / 100.0).toInt().coerceAtLeast(1)
            val newHp = max(0, enemy.hp - dmg)
            // EMP:命中即眩晕附近敌人;火焰:点燃燃烧
            val stun = if (bullet.type == BulletType.EMP) GameConfig.EMP_STUN_TIME else enemy.stunTimer
            val burn = if (bullet.type == BulletType.FLAME) GameConfig.FLAME_BURN_TIME else enemy.burnTimer
            en = en.toMutableList().apply {
                this[hitIndex] = enemy.copy(hp = newHp, alive = newHp > 0, stunTimer = stun, burnTimer = burn)
            }
            remaining = consumeBullet(remaining, bullet)
        }
    }

    // EMP:爆炸范围内所有敌人眩晕 + 伤害(即使未直接命中)
    val empBullets = remaining.filter { it.type == BulletType.EMP }
    if (empBullets.isNotEmpty()) {
        empBullets.forEach { bullet ->
            en = en.map { enemy ->
                if (enemy.alive && enemy.id != bullet.ownerId) {
                    val dx = enemy.x - bullet.x
                    val dz = enemy.z - bullet.z
                    val d = sqrt(dx * dx + dz * dz)
                    if (d < GameConfig.EMP_RADIUS) {
                        // 眩晕 + 爆炸伤害
                        val dmg = (bullet.damage * (100 - enemy.armor) / 100.0).toInt().coerceAtLeast(1)
                        val newHp = max(0, enemy.hp - dmg)
                        enemy.copy(hp = newHp, alive = newHp > 0, stunTimer = GameConfig.EMP_STUN_TIME)
                    } else enemy
                } else enemy
            }
            remaining.remove(bullet)
        }
    }

    // 导弹 / 榴弹:命中后范围爆炸伤害
    val exploded = remaining.filter { it.type == BulletType.MISSILE || it.type == BulletType.GRENADE }
    if (exploded.isNotEmpty()) {
        exploded.forEach { bullet ->
            val blast = stateEnemiesHit(p, en, bullet)
            p = blast.player
            en = blast.enemies
            remaining.remove(bullet)
        }
    }

    return BulletHitResult(p, en, remaining)
}

private fun consumeBullet(remaining: MutableList<BulletState>, bullet: BulletState): MutableList<BulletState> {
    if (bullet.type == BulletType.LASER && bullet.pierce > 0) {
        // 穿透:更新穿透次数
        return remaining.map {
            if (it.id == bullet.id) it.copy(pierce = it.pierce - 1) else it
        }.toMutableList()
    }
    remaining.remove(bullet)
    return remaining
}

// 导弹爆炸范围伤害
private fun stateEnemiesHit(
    player: TankState,
    enemies: List<TankState>,
    bullet: BulletState
): BlastResult {
    var p = player
    var en = enemies
    // 对范围内的敌人
    en = en.map { enemy ->
        if (enemy.alive && enemy.id != bullet.ownerId) {
            val dx = enemy.x - bullet.x
            val dz = enemy.z - bullet.z
            val d = sqrt(dx * dx + dz * dz)
            if (d < GameConfig.MISSILE_RADIUS) {
                val dmg = (bullet.damage * (100 - enemy.armor) / 100.0).toInt().coerceAtLeast(1)
                val newHp = max(0, enemy.hp - dmg)
                enemy.copy(hp = newHp, alive = newHp > 0)
            } else enemy
        } else enemy
    }
    // 对玩家
    if (p.alive && p.id != bullet.ownerId) {
        val dx = p.x - bullet.x
        val dz = p.z - bullet.z
        val d = sqrt(dx * dx + dz * dz)
        if (d < GameConfig.MISSILE_RADIUS && p.shieldTimer <= 0.0 && p.y <= GameConfig.JUMP_DODGE_HEIGHT) {
            val dmg = (bullet.damage * (100 - p.armor) / 100.0).toInt().coerceAtLeast(1)
            p = p.copy(hp = max(0, p.hp - dmg), alive = p.hp - dmg > 0)
        }
    }
    return BlastResult(p, en)
}

private data class BlastResult(
    val player: TankState,
    val enemies: List<TankState>
)

// 近战电击:直射瞄准炮管指向的敌人,并从该敌人连锁到附近敌人。
// 返回更新后的敌人列表、连锁点(含玩家起点)和伤害飘字
private data class MeleeResult(
    val enemies: List<TankState>,
    val chainPoints: List<Pair<Double, Double>>,  // [0]=玩家,后续为连锁敌人
    val damageNumbers: List<DamageNumberState>
)

private fun applyMelee(
    player: TankState,
    enemies: List<TankState>,
    nextId: Int
): MeleeResult {
    val dirX = sin(player.turretAngle)
    val dirZ = cos(player.turretAngle)

    // 1) 直射:炮管指向方向上、射程内的敌人,选最近的作为主目标
    val inRange = enemies.filter { enemy ->
        enemy.alive && enemy.shieldTimer <= 0.0
    }.map { enemy ->
        val dx = enemy.x - player.x
        val dz = enemy.z - player.z
        enemy to (dx * dx + dz * dz)
    }.filter { (_, d2) -> d2 < GameConfig.MELEE_RADIUS * GameConfig.MELEE_RADIUS }
        .sortedBy { it.second }

    // 炮管指向最近的敌人(与炮管角度最接近的优先)
    val mainTarget = inRange.minByOrNull { (enemy, d2) ->
        val dx = enemy.x - player.x
        val dz = enemy.z - player.z
        val dist = sqrt(d2)
        val dot = (dx * dirX + dz * dirZ) / (dist + 0.0001)
        // 以"偏离角度"为主排序:先筛射程,再按角度
        (1.0 - dot)
    }

    val result = enemies.toMutableList()
    val chainPoints = mutableListOf<Pair<Double, Double>>(player.x to player.z)
    val damageNumbers = mutableListOf<DamageNumberState>()
    var id = nextId

    fun damageEnemy(target: TankState, dmg: Int) {
        val realDmg = (dmg * (100 - target.armor) / 100.0).toInt().coerceAtLeast(1)
        val idx = result.indexOfFirst { it.id == target.id }
        if (idx >= 0) {
            val enemy = result[idx]
            val newHp = max(0, enemy.hp - realDmg)
            result[idx] = enemy.copy(hp = newHp, alive = newHp > 0)
            chainPoints.add(target.x to target.z)
            damageNumbers += DamageNumberState(
                id = id, x = target.x, z = target.z,
                amount = realDmg, isPlayer = true, life = 1.0
            )
            id += 1
        }
    }

    // 主目标:全额伤害
    if (mainTarget != null) {
        damageEnemy(mainTarget.first, GameConfig.MELEE_DAMAGE)

        // 2) 连锁:从主目标向附近敌人扩散,每次取最近的未命中敌人
        var last = mainTarget.first
        var chainCount = 0
        while (chainCount < 3) {
            val lastX = last.x
            val lastZ = last.z
            val nextEnemy = result.filter { it.alive && it.shieldTimer <= 0.0 }
                .filter { e -> chainPoints.none { (cx, cz) -> abs(cx - e.x) < 0.5 && abs(cz - e.z) < 0.5 } }
                .minByOrNull { e ->
                    val dx = e.x - lastX
                    val dz = e.z - lastZ
                    dx * dx + dz * dz
                }
            // 连锁距离限制
            if (nextEnemy == null) break
            val dx = nextEnemy.x - lastX
            val dz = nextEnemy.z - lastZ
            if (dx * dx + dz * dz > GameConfig.MELEE_RADIUS * GameConfig.MELEE_RADIUS) break
            damageEnemy(nextEnemy, (GameConfig.MELEE_DAMAGE * 0.6).toInt())
            last = nextEnemy
            chainCount += 1
        }
    }

    return MeleeResult(result, chainPoints, damageNumbers)
}

private fun circleHit(bx: Double, bz: Double, tx: Double, tz: Double): Boolean {
    val dx = bx - tx
    val dz = bz - tz
    val r = GameConfig.TANK_RADIUS + GameConfig.BULLET_RADIUS
    return dx * dx + dz * dz < r * r
}

// 玩家移动 + 跳跃 + 技能
private fun updatePlayer(
    tank: TankState,
    input: PlayerInput,
    dt: Double,
    obstacles: List<ObstacleState>
): TankState {
    var angle = tank.angle
    var x = tank.x
    var z = tank.z

    if (input.left) angle += GameConfig.TURN_SPEED * dt
    if (input.right) angle -= GameConfig.TURN_SPEED * dt

    val move = when {
        input.forward && !input.backward -> 1.0
        input.backward && !input.forward -> -0.6
        else -> 0.0
    }

    val speedMult = if (tank.speedTimer > 0.0) GameConfig.SPEED_BUFF_MULT else 1.0

    // W/S 沿车身(底盘)朝向移动,鼠标仅控制炮塔瞄准
    val moveAngle = angle

    val nx = x + sin(moveAngle) * tank.speed * speedMult * move * dt
    val nz = z + cos(moveAngle) * tank.speed * speedMult * move * dt

    val airborne = tank.y > 0.5
    if (!airborne) {
        if (!obstacles.any { circleIntersectsObstacle(nx, z, GameConfig.TANK_RADIUS, it) }) {
            x = nx
        }
        if (!obstacles.any { circleIntersectsObstacle(x, nz, GameConfig.TANK_RADIUS, it) }) {
            z = nz
        }
    } else {
        x = nx
        z = nz
    }

    var y = tank.y
    var vy = tank.vy
    if (input.jump && y <= 0.01 && vy <= 0.0) {
        vy = GameConfig.JUMP_SPEED
    }
    vy += GameConfig.GRAVITY * dt
    y += vy * dt
    if (y < 0.0) {
        y = 0.0
        vy = 0.0
    }

    val turretAngle = input.aimAngle ?: tank.turretAngle

    return tank.copy(
        x = clampToArena(x),
        z = clampToArena(z),
        y = y,
        vy = vy,
        angle = angle,
        turretAngle = turretAngle
    )
}

// 敌人 AI:避障 + 路径分散 + 朝向玩家移动,返回是否尝试开火
private fun updateEnemy(
    enemy: TankState,
    player: TankState,
    allEnemies: List<TankState>,
    dt: Double,
    obstacles: List<ObstacleState>
): Pair<TankState, Boolean> {
    val dx = player.x - enemy.x
    val dz = player.z - enemy.z
    val dist = sqrt(dx * dx + dz * dz)
    val baseAngle = atan2(dx, dz)

    val laneBias = when (enemy.id % 4) {
        0 -> 0.0
        1 -> 0.5
        2 -> -0.5
        else -> 0.25
    }
    val converge = (dist / 20.0).coerceIn(0.0, 1.0)
    var desiredAngle = normalizeAngle(baseAngle + laneBias * (1.0 - converge))

    val lookAhead = 5.0
    if (isPathBlocked(enemy.x, enemy.z, desiredAngle, lookAhead, obstacles)) {
        val probeOffsets = listOf(0.5, -0.5, 1.0, -1.0, 1.6, -1.6, PI)
        for (offset in probeOffsets) {
            val candidate = normalizeAngle(desiredAngle + offset)
            if (!isPathBlocked(enemy.x, enemy.z, candidate, lookAhead, obstacles)) {
                desiredAngle = candidate
                break
            }
        }
    }

    var repulsion = 0.0
    val separationDist = GameConfig.TANK_RADIUS * 2.6
    allEnemies.forEach { other ->
        if (other.id == enemy.id || !other.alive) return@forEach
        val ox = enemy.x - other.x
        val oz = enemy.z - other.z
        val d = sqrt(ox * ox + oz * oz)
        if (d < separationDist && d > 0.001) {
            val awayAngle = atan2(ox, oz)
            val strength = (1.0 - d / separationDist) * 0.7
            repulsion += normalizeAngle(awayAngle - desiredAngle) * strength
        }
    }
    desiredAngle = normalizeAngle(desiredAngle + repulsion)

    var angle = enemy.angle
    val diff = normalizeAngle(desiredAngle - angle)
    val turn = max(-GameConfig.TURN_SPEED * dt, min(GameConfig.TURN_SPEED * dt, diff))
    angle = normalizeAngle(angle + turn)

    // EMP 眩晕:无法移动和开火
    val stunned = enemy.stunTimer > 0.0
    var move = 0.0
    if (!stunned) {
        if (dist > 10.0) move = 1.0
        else if (dist < 3.0) move = -0.5
        else move = 0.3
    }

    if (isPathBlocked(enemy.x, enemy.z, angle, GameConfig.TANK_RADIUS * 1.8, obstacles)) {
        move *= 0.25
    }

    val speedMult = if (enemy.speedTimer > 0.0) GameConfig.SPEED_BUFF_MULT else 1.0

    val nx = enemy.x + sin(angle) * enemy.speed * speedMult * move * dt
    val nz = enemy.z + cos(angle) * enemy.speed * speedMult * move * dt
    var x = enemy.x
    var z = enemy.z
    if (!obstacles.any { circleIntersectsObstacle(nx, z, GameConfig.TANK_RADIUS, it) }) {
        x = nx
    }
    if (!obstacles.any { circleIntersectsObstacle(x, nz, GameConfig.TANK_RADIUS, it) }) {
        z = nz
    }

    val wantsToFire =
        !stunned &&
            abs(normalizeAngle(baseAngle - angle)) < 0.2 && dist < 30.0 && dist > 3.0

    return enemy.copy(
        x = clampToArena(x),
        z = clampToArena(z),
        angle = angle,
        turretAngle = angle
    ) to wantsToFire
}

private fun isPathBlocked(
    x: Double,
    z: Double,
    angle: Double,
    lookAhead: Double,
    obstacles: List<ObstacleState>
): Boolean {
    val limit = GameConfig.ARENA_HALF - GameConfig.TANK_RADIUS
    val steps = 6
    for (i in 1..steps) {
        val t = lookAhead * i / steps
        val px = x + sin(angle) * t
        val pz = z + cos(angle) * t
        if (abs(px) > limit || abs(pz) > limit) return true
        if (obstacles.any { circleIntersectsObstacle(px, pz, GameConfig.TANK_RADIUS * 0.9, it) }) return true
    }
    return false
}

private fun normalizeAngle(a: Double): Double {
    var r = a % (2 * PI)
    if (r > PI) r -= 2 * PI
    if (r < -PI) r += 2 * PI
    return r
}

// 在随机空位生成道具(避开所有坦克)
private fun spawnPowerup(nextId: Int, tanks: List<TankState>): PowerupState? {
    val types = PowerupType.entries
    val avoidDist = GameConfig.TANK_RADIUS + GameConfig.POWERUP_RADIUS + 1.0
    for (attempt in 0 until 20) {
        val margin = GameConfig.ARENA_HALF - 8.0
        val x = (pseudoRandom(nextId * 3 + attempt * 11) * 2 - 1) * margin
        val z = (pseudoRandom(nextId * 7 + attempt * 5) * 2 - 1) * margin
        // 避开所有存活坦克
        val nearTank = tanks.any { tank ->
            if (!tank.alive) return@any false
            val dx = x - tank.x
            val dz = z - tank.z
            dx * dx + dz * dz < avoidDist * avoidDist
        }
        if (!nearTank) {
            val type = types[(nextId * 7 + attempt) % types.size]
            return PowerupState(
                id = nextId,
                type = type,
                x = x,
                z = z,
                life = GameConfig.POWERUP_LIFE
            )
        }
    }
    return null
}

// 在 GameReducer.kt 末尾添加
private fun applyOblivionDamage(
    player: TankState,
    enemies: List<TankState>,
    bullets: List<BulletState>,
    nextId: Int
): Triple<TankState, List<TankState>, List<DamageNumberState>> {
    var p = player
    var en = enemies
    val damageNumbers = mutableListOf<DamageNumberState>()
    var id = nextId
    
    bullets.filter { it.type == BulletType.OBLIVION }.forEach { bullet ->
        // 全屏伤害
        en = en.map { enemy ->
            if (enemy.alive && enemy.id != bullet.ownerId) {
                val dist = sqrt((enemy.x - bullet.x).let { it*it } + (enemy.z - bullet.z).let { it*it })
                val dmg = if (dist < GameConfig.OBLIVION_RADIUS) {
                    (GameConfig.OBLIVION_DAMAGE * (1.0 - dist / GameConfig.OBLIVION_RADIUS)).toInt().coerceAtLeast(5000)
                } else {
                    GameConfig.OBLIVION_DAMAGE / 10
                }
                val newHp = max(0, enemy.hp - dmg)
                
                // 生成伤害飘字
                damageNumbers += DamageNumberState(
                    id = id,
                    x = enemy.x,
                    z = enemy.z,
                    amount = dmg,
                    isPlayer = true,
                    life = 2.0
                )
                id += 1
                
                enemy.copy(
                    hp = newHp,
                    alive = newHp > 0,
                    stunTimer = 5.0,
                    burnTimer = 5.0
                )
            } else enemy
        }
        
        // 对 Boss 级敌人额外伤害
        en = en.map { enemy ->
            if (enemy.alive && enemy.hp > 1000) {
                enemy.copy(hp = max(0, enemy.hp - 50000))
            } else enemy
        }
    }
    
    return Triple(p, en, damageNumbers)
}

private data class BulletHitResult(
    val player: TankState,
    val enemies: List<TankState>,
    val bullets: List<BulletState>
)
